import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Facebook, Mail, Phone, MapPin, ArrowRight, Lock, BadgeCheck, Star, Recycle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { ProfessionalModeLink } from '@/components/enterprise/ProfessionalModeToggle';
import { CredibilityBadges } from '@/components/seo/CredibilityBadges';
import { useToast } from '@/hooks/use-toast';
import { trackGenericEvent, trackLeadCapture, setStoredEmail, setConsentState, hasAnyConsent } from '@/lib/crm/track';
import { useIsMobile } from '@/hooks/use-mobile';

// ─── Data ────────────────────────────────────────────────────
const footerSections = [
  {
    title: 'Quem Somos',
    links: [
      { label: 'Nossa História', href: '/filosofia' },
      { label: 'Qualidade & Segurança', href: '/seguranca-testes' },
      { label: 'Ciência & Compliance', href: '/nossa-ciencia' },
      { label: 'Responsabilidade', href: '/sustentabilidade' },
      { label: 'Artigos & Conteúdo', href: '/estudos-evidencias' },
    ],
  },
  {
    title: 'Produtos',
    links: [
      { label: 'Todos os Produtos', href: '/loja' },
      { label: 'Kits & Rotinas', href: '/kits' },
    ],
  },
  {
    title: 'Profissionais',
    links: [
      { label: 'Médicos e Prescritores', href: '/profissionais-saude' },
      { label: 'Clínicas e Hospitais', href: '/hospitais' },
      { label: 'Materiais Técnicos', href: '/treinamentos' },
    ],
  },
  {
    title: 'Parcerias',
    links: [
      { label: 'Seja Revendedor', href: '/revendedores' },
      { label: 'Programa de Afiliados', href: '/afiliados' },
      { label: 'Trabalhe Conosco', href: '/parcerias' },
    ],
  },
  {
    title: 'Suporte',
    links: [
      { label: 'FAQ', href: '/faq' },
      { label: 'Garantia de Satisfação', href: '/garantia' },
      { label: 'Trocas e Devoluções', href: '/politicas/troca-devolucao' },
      { label: 'Frete e Envio', href: '/politicas/frete-envio' },
      { label: 'Contato', href: '/contato' },
      { label: 'Política de Privacidade', href: '/politicas/privacidade' },
      { label: 'Termos de Serviço', href: '/politicas/termos-de-servico' },
      { label: 'Exclusão de Dados', href: '/exclusao-de-dados' },
    ],
  },
];

// ─── Newsletter sub-component ────────────────────────────────
const FooterNewsletter = () => {
  const [email, setEmail] = useState('');
  const [hint, setHint] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    trackGenericEvent('newsletter_view');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHint(null);
    if (!email) return;

    setIsSubmitting(true);
    try {
      // Grava identidade e consentimento ANTES do envio, para o gate
      // hasAnyConsent() de trackLeadCapture deixar o evento chegar ao CRM.
      setStoredEmail(email);
      setConsentState({ email_opt_in: true });
      if (!hasAnyConsent()) {
        throw new Error('consent_not_recorded');
      }
      trackGenericEvent('newsletter_submit', { email_domain: email.split('@')[1] });
      trackLeadCapture('footer', { email }, { emailOptIn: true });
      setIsDone(true);
      toast({ title: 'Cadastro realizado!', description: 'Você receberá novidades e conteúdos exclusivos.' });
      setEmail('');
    } catch {
      toast({
        title: 'Não consegui concluir o cadastro',
        description: 'Tente novamente em alguns instantes.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border-b border-secondary-foreground/10">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="text-lg md:text-xl font-semibold mb-2">
            Conteúdo sobre pele sensível, direto no seu e-mail
          </h3>
          <p className="text-sm text-secondary-foreground/70 mb-4">
            Dicas de cuidado, novidades e ciência acessível — sem spam.
          </p>
          {isDone ? (
            <p className="text-sm text-secondary-foreground/90 max-w-md mx-auto">
              Cadastro confirmado. Obrigado por estar por perto.
            </p>
          ) : (
          <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-3">
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Seu melhor e-mail"
                value={email}
                onChange={e => { setEmail(e.target.value); setHint(null); }}
                className="bg-secondary-foreground text-secondary border-secondary-foreground/20 placeholder:text-secondary/60"
                required
                disabled={isSubmitting}
              />
              <Button type="submit" className="gap-2" disabled={isSubmitting}>
                {isSubmitting ? 'Enviando...' : 'Receber'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            {hint && (
              <p className="text-xs text-brand-amber text-left">{hint}</p>
            )}
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Mobile accordion footer ─────────────────────────────────
const FooterMobile = () => (
  <div className="container mx-auto px-4 py-8 md:hidden">
    <Accordion type="multiple" className="space-y-0">
      {footerSections.map((section) => (
        <AccordionItem key={section.title} value={section.title} className="border-secondary-foreground/10">
          <AccordionTrigger className="text-secondary-foreground hover:no-underline py-3 text-sm font-semibold">
            {section.title}
          </AccordionTrigger>
          <AccordionContent>
            <ul className="space-y-2 pb-2">
              {section.links.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>

    {/* Contact info */}
    <div className="mt-6 space-y-2">
      <a href="mailto:sac@nathof.com.br" className="flex items-center gap-2 text-sm text-secondary-foreground/80 hover:text-primary transition-colors">
        <Mail className="w-4 h-4" /> sac@nathof.com.br
      </a>
      <a className="flex items-center gap-2 text-sm text-secondary-foreground/80 hover:text-primary transition-colors" href="tel:+5519984150022">
        <Phone className="w-4 h-4" /> (19) 9 8415-0022
      </a>
    </div>
  </div>
);

// ─── Desktop columns footer ──────────────────────────────────
const FooterDesktop = () => (
  <div className="container mx-auto px-4 py-12 md:py-16 hidden md:block">
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12">
      {footerSections.map((section) => (
        <div key={section.title}>
          <h4 className="font-semibold mb-4">{section.title}</h4>
          <ul className="space-y-3 text-sm">
            {section.links.map((link) => (
              <li key={link.href}>
                <Link to={link.href} className="text-secondary-foreground/80 hover:text-primary transition-colors">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          {section.title === 'Suporte' && (
            <div className="mt-6 space-y-2">
              <a href="mailto:sac@nathof.com.br" className="flex items-center gap-2 text-sm text-secondary-foreground/80 hover:text-primary transition-colors">
                <Mail className="w-4 h-4" /> sac@nathof.com.br
              </a>
              <a className="flex items-center gap-2 text-sm text-secondary-foreground/80 hover:text-primary transition-colors" href="tel:+5519984150022">
                <Phone className="w-4 h-4" /> (19) 9 8415-0022
              </a>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ─── Main Footer export ──────────────────────────────────────
export const Footer = () => {
  return (
    <footer className="bg-secondary text-secondary-foreground">
      <FooterNewsletter />
      <FooterMobile />
      <FooterDesktop />

      {/* Credibility badges row */}
      <div className="container mx-auto px-4">
        <div className="py-5 border-t border-secondary-foreground/10">
          <CredibilityBadges variant="footer" />
        </div>
      </div>

      {/* Trust badges row */}
      <div className="container mx-auto px-4">
        <div className="py-5 border-t border-secondary-foreground/10">
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
            {[
              { icon: Lock, label: 'Site Seguro SSL', to: undefined },
              { icon: BadgeCheck, label: 'Notificado na ANVISA', to: undefined },
              { icon: Star, label: '4.8/5 Avaliações', to: '/avaliacoes' },
              { icon: Recycle, label: 'Embalagem Sustentável', to: undefined },
            ].map(({ icon: Icon, label, to }) => {
              const inner = (
                <>
                  <Icon className={`w-3.5 h-3.5 ${label.includes('Avaliações') ? 'text-brand-amber fill-brand-amber' : 'text-secondary-foreground/70'}`} />
                  {label}
                </>
              );
              const baseClass = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-secondary-foreground/20 text-[11px] md:text-xs text-secondary-foreground/85';
              return to ? (
                <Link
                  key={label}
                  to={to}
                  aria-label="Ver todas as avaliações"
                  className={`${baseClass} hover:text-primary hover:border-primary/40 transition-colors`}
                >
                  {inner}
                </Link>
              ) : (
                <span key={label} className={baseClass}>
                  {inner}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Social & Brand Row */}
      <div className="container mx-auto px-4">
        <div className="py-6 border-t border-secondary-foreground/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Link to="/" className="inline-block">
                <span className="text-2xl font-semibold tracking-tight">
                  nat<span className="text-primary">·</span>hof
                </span>
              </Link>
              <span className="text-sm text-secondary-foreground/60 hidden md:inline">
                Ciência restauradora para pele sensível
              </span>
            </div>
            <div className="flex gap-4">
              <a href="https://instagram.com/nat.hof.care" target="_blank" rel="noopener noreferrer" className="text-secondary-foreground/70 hover:text-primary transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com/company/nathof" target="_blank" rel="noopener noreferrer" className="text-secondary-foreground/70 hover:text-primary transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://facebook.com/nathof" target="_blank" rel="noopener noreferrer" className="text-secondary-foreground/70 hover:text-primary transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-secondary-foreground/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-secondary-foreground/60">
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
              <span>© {new Date().getFullYear()} NAT HOF Dermocosméticos. Todos os direitos reservados.</span>
              <span className="hidden md:inline">|</span>
              <span>NATHOF COMERCIO DE PRODUTOS DE PERFUMARIA E HIGIENE PESSOAL LTDA · CNPJ 54.475.581/0001-47 · IE 135.723.375.116 · IM 953063-0</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/politicas/privacidade" className="hover:text-primary transition-colors">Privacidade</Link>
              <Link to="/politicas/termos-de-servico" className="hover:text-primary transition-colors">Termos</Link>
              <Link to="/exclusao-de-dados" className="hover:text-primary transition-colors">Exclusão de Dados</Link>
              <Link to="/mapa-do-site" className="hover:text-primary transition-colors">Mapa do Site</Link>
            </div>
          </div>
          <div className="mt-4 flex flex-col md:flex-row items-center justify-center gap-2 text-xs text-secondary-foreground/60">
            <div className="flex items-center gap-2">
              <MapPin className="w-3 h-3" />
              <span className="text-center md:text-left">R. Dom Francisco de Campos Barreto, 282 - Sala D, Nova Campinas, Campinas-SP, CEP 13.092-160</span>
            </div>
            <span className="hidden md:inline mx-2">|</span>
            <ProfessionalModeLink />
          </div>
        </div>
      </div>
    </footer>
  );
};
